import { useEffect, useState } from "react";
import { LOOP_STAGES, type LoopStageId } from "@/content/site";
import { cn } from "@/lib/cn";

/**
 * The annunciator rail — the page's signature.
 *
 * A control room's legend strip is a row of lamps that sit dark until something
 * needs your eye. This is that strip, applied to the argument: the four loop
 * stages run down the left margin, all of them quiet, and only the one you are
 * reading is lit. It is the product's own ISA-101 rule used as page furniture,
 * and it doubles as a table of contents that says where you are.
 *
 * Scroll position comes from an IntersectionObserver over the stage sections, so
 * there is no scroll handler firing on every frame. It renders with the first
 * stage lit, which is what the prerendered HTML shows — no hydration mismatch.
 */
export function LoopRail({ stageIds }: { stageIds: readonly LoopStageId[] }) {
  // Fall back to the first declared stage rather than to a literal: the stage
  // ids live in content/site.ts and a hard-coded one here goes stale silently
  // the moment the pipeline is renamed.
  const [active, setActive] = useState<LoopStageId>(stageIds[0] ?? LOOP_STAGES[0].id);

  useEffect(() => {
    const sections = stageIds
      .map((id) => document.getElementById(`stage-${id}`))
      .filter((el): el is HTMLElement => el != null);
    if (!sections.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // The stage whose section occupies the middle band of the viewport wins.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id.replace("stage-", "") as LoopStageId);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.01, 0.5, 1] },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [stageIds]);

  return (
    <nav
      aria-label="Loop stages"
      className="pointer-events-none sticky top-28 hidden h-0 xl:block"
    >
      <ol className="pointer-events-auto -ml-52 flex w-44 flex-col gap-px border-l border-line/12">
        {LOOP_STAGES.filter((s) => stageIds.includes(s.id)).map((s) => {
          const on = s.id === active;
          return (
            <li key={s.id}>
              <a
                href={`#stage-${s.id}`}
                className={cn(
                  "group flex items-start gap-3 border-l-2 py-2.5 pl-4 transition-colors duration-200",
                  on ? "border-ember" : "border-transparent hover:border-line/30",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-200",
                    on ? "bg-ember" : "bg-line/25 group-hover:bg-line/45",
                  )}
                />
                <span className="flex min-w-0 flex-col">
                  <span
                    className={cn(
                      "font-mono text-rail font-medium uppercase transition-colors duration-200",
                      on ? "text-bone" : "text-bone-faint",
                    )}
                  >
                    {s.label}
                  </span>
                  <span className="mt-1 text-xs leading-tight text-bone-faint">{s.note}</span>
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** The same strip laid flat, for narrow viewports where there is no margin to use. */
export function LoopStrip({ className }: { className?: string }) {
  return (
    <ol
      className={cn(
        "flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-rail uppercase text-bone-faint",
        className,
      )}
    >
      {LOOP_STAGES.map((s, i) => (
        <li key={s.id} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden="true" className="mr-3 h-px w-5 bg-line/20" />}
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-line/30" />
          {s.label}
        </li>
      ))}
    </ol>
  );
}
